import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './PageHeader';
import ConfirmActionModal from './ConfirmActionModal';
import {
  Bell, Lock, Shield, Database,
  HelpCircle, Edit, ChevronRight, X, Check, Sparkle, Link as LinkIcon,
} from './Icons';
import useHaptics from '../hooks/useHaptics';
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api';
import { openExternal } from '../lib/native';
import pkg from '../../package.json';
import './Profile.css';

const SUPPORT_EMAIL = 'support@hourtrix.com';
const TERMS_URL = process.env.REACT_APP_TERMS_URL || 'https://hourtrix.com/terms';
const PRIVACY_URL = process.env.REACT_APP_PRIVACY_URL || 'https://hourtrix.com/privacy';

const formatRenewalDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    }).format(d);
  } catch { return null; }
};

const WEEK_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || 'AM';
};

const todayWeekdayIdx = () => new Date().getDay();

function Donut({ pct = 0, size = 56, stroke = 5 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, pct)) / 100) * c;
  return (
    <svg className="hx-donut" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
      <circle cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke="var(--rule-soft)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke="var(--gold-500)" strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={`${dash} ${c - dash}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
    </svg>
  );
}

function EditModal({ open, initial, onClose, onSave }) {
  const [name, setName] = useState(initial.display_name || '');
  const [bio, setBio] = useState(initial.bio || '');
  const [avatar, setAvatar] = useState(initial.avatar_url || null);
  const [busy, setBusy] = useState(false);
  const firstInputRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName(initial.display_name || '');
      setBio(initial.bio || '');
      setAvatar(initial.avatar_url || null);
      setTimeout(() => firstInputRef.current?.focus(), 60);
    } else {
      triggerRef.current?.focus();
    }
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setAvatar(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onSave({ display_name: name.trim() || initial.display_name, bio: bio.trim(), avatar_url: avatar });
    setBusy(false);
  };

  return (
    <div className="hx-sheet-overlay" onClick={onClose}>
      <form className="hx-sheet" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <header className="hx-sheet-head">
          <h3 className="h-2">Edit Profile</h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </header>
        <div className="hx-sheet-body">
          <label className="hx-avatar-upload">
            <span className="hx-avatar lg">
              {avatar ? <img src={avatar} alt="Your avatar" /> : <span aria-hidden>{getInitials(name)}</span>}
            </span>
            <span className="eyebrow hx-avatar-cta">Change photo</span>
            <input type="file" accept="image/*" onChange={onPickFile} hidden />
          </label>
          <label className="hx-field">
            <span className="eyebrow">Display name</span>
            <input ref={firstInputRef} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
          </label>
          <label className="hx-field">
            <span className="eyebrow">Bio</span>
            <textarea rows="3" value={bio} onChange={(e) => setBio(e.target.value)}
              placeholder="A short line about your focus." />
          </label>
        </div>
        <footer className="hx-sheet-foot">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy}><Check size={16} /> Save</button>
        </footer>
      </form>
    </div>
  );
}

function SecuritySheet({ user, onClose }) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteBtnRef = useRef(null);
  const bodyRef = useRef(null);
  const email = user?.email || '—';
  const signInMethod = user?.auth_provider === 'google' ? 'Google' : 'Magic link';
  const hasPassword = Boolean(user?.has_password);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !deleteOpen) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, deleteOpen]);

  // Force the sheet body to start at top on open — sheet content can overflow
  // viewport on smaller screens and was landing mid-scroll.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    try { el.scrollTo({ top: 0, behavior: 'instant' }); }
    catch { el.scrollTop = 0; }
  }, []);

  const onConfirmDelete = async () => {
    const res = await apiDelete('/api/user/account', {
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirmation_text: 'DELETE MY ACCOUNT' }),
    });
    if (!res.ok) throw new Error('Something went wrong. Please try again.');
    try {
      const native = await import('../lib/native');
      if (native.clearStoredToken) await native.clearStoredToken();
    } catch {}
    try { localStorage.clear(); } catch {}
    window.location.href = '/';
  };

  return (
    <>
      <div className="hx-sheet-overlay" onClick={onClose}>
        <div className="hx-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <header className="hx-sheet-head">
            <h3 className="h-2">Account & Security</h3>
            <button type="button" className="icon-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
          </header>
          <div className="hx-sheet-body" ref={bodyRef}>
            <div className="hx-info-field"><span className="eyebrow">Email</span><span className="hx-info-value">{email}</span></div>
            <div className="hx-info-field"><span className="eyebrow">Sign-in method</span><span className="hx-info-value">{signInMethod}</span></div>

            <div className="hx-danger-zone">
              <div className="hx-danger-eyebrow">DANGER ZONE</div>
              <p className="hx-danger-desc">
                Deleting your account permanently removes all activities, logs, journal entries, and patterns. This cannot be undone.
              </p>
              <button
                ref={deleteBtnRef}
                type="button"
                className="hx-danger-link"
                onClick={() => setDeleteOpen(true)}
              >
                Delete account…
              </button>
            </div>
          </div>
          <footer className="hx-sheet-foot single">
            <button type="button" className="btn-ghost" onClick={onClose}>Done</button>
          </footer>
        </div>
      </div>

      <ConfirmActionModal
        open={deleteOpen}
        title="Delete your account"
        message="This will permanently delete your account and all your data. This cannot be undone."
        confirmWord="DELETE MY ACCOUNT"
        confirmLabel="Delete Account"
        destructive
        requirePassword={hasPassword}
        hasPassword={hasPassword}
        onClose={() => { setDeleteOpen(false); setTimeout(() => deleteBtnRef.current?.focus(), 50); }}
        onConfirm={onConfirmDelete}
      />
    </>
  );
}

function DataSheet({ userId, onClose, onFlashToast }) {
  const [exporting, setExporting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const freshStartBtnRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !resetOpen) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, resetOpen]);

  const onExport = async () => {
    setExporting(true);
    try {
      const [logsRes, reflRes] = await Promise.allSettled([
        apiGet(`/api/logs/${userId}`), apiGet('/api/reflections'),
      ]);
      const rawLogs = logsRes.status === 'fulfilled' && logsRes.value?.ok
        ? await logsRes.value.json().catch(() => []) : [];
      const rawReflections = reflRes.status === 'fulfilled' && reflRes.value?.ok
        ? await reflRes.value.json().catch(() => []) : [];

      const cleanLogs = rawLogs.map((l) => ({
        log_id: l.log_id, activity_id: l.activity_id, date: l.date,
        actual_minutes: l.actual_minutes, source: l.source, created_at: l.created_at,
      }));
      const cleanReflections = rawReflections.map((r) => ({
        reflection_id: r.reflection_id, date: r.date, body: r.body,
        summary: r.summary, mood: r.mood, themes: r.themes, created_at: r.created_at,
      }));

      const todayIso = new Date().toISOString().slice(0, 10);
      const json = JSON.stringify({ exported_at: new Date().toISOString(), logs: cleanLogs, reflections: cleanReflections }, null, 2);
      const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url; a.download = `hourtrix-export-${todayIso}.json`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      onFlashToast('Export failed. Please try again.');
    } finally { setExporting(false); }
  };

  const onConfirmReset = async () => {
    setResetting(true);
    try {
      const res = await apiPost(`/api/user/reset/${userId}`, {});
      if (!res.ok) throw new Error('Reset failed. Please try again.');
      onFlashToast('Your data has been reset.');
      setTimeout(() => window.location.reload(), 1800);
    } finally { setResetting(false); }
  };

  return (
    <>
      <div className="hx-sheet-overlay" onClick={onClose}>
        <div className="hx-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <header className="hx-sheet-head">
            <h3 className="h-2">Data & Privacy</h3>
            <button type="button" className="icon-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
          </header>
          <div className="hx-sheet-body">
            <p className="hx-info-body">Your reflections are processed by an AI to extract activities and patterns. Logs and reflections stay in your account; we don't share them.</p>
            <button type="button" className="btn-primary" onClick={onExport} disabled={exporting}>
              {exporting ? 'Exporting…' : 'Export your data'}
            </button>
            <button
              ref={freshStartBtnRef}
              type="button"
              className="btn-ghost btn-danger-ghost"
              onClick={() => setResetOpen(true)}
              disabled={resetting}
            >
              {resetting ? 'Resetting…' : 'Fresh Start'}
            </button>
          </div>
          <footer className="hx-sheet-foot single">
            <button type="button" className="btn-ghost" onClick={onClose}>Done</button>
          </footer>
        </div>
      </div>

      <ConfirmActionModal
        open={resetOpen}
        title="Fresh Start"
        message="This deletes all your logs and reflections but keeps your account. This cannot be undone."
        confirmWord="RESET"
        confirmLabel="Reset Data"
        destructive
        requirePassword={false}
        onClose={() => { setResetOpen(false); setTimeout(() => freshStartBtnRef.current?.focus(), 50); }}
        onConfirm={onConfirmReset}
      />
    </>
  );
}

function BillingSheet({ onClose, onFlashToast }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entitlement, setEntitlement] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const cancelBtnRef = useRef(null);

  const fetchEntitlement = async () => {
    try {
      const res = await apiGet('/api/user/entitlement');
      if (res.ok) {
        const data = await res.json();
        setEntitlement(data);
      }
    } catch { /* keep null */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchEntitlement(); }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !cancelOpen) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, cancelOpen]);

  const status = entitlement?.status || null;
  const isActive = status === 'active' || status === 'trial_active';
  const hasSubscription = isActive && entitlement?.access_source === 'stripe';
  const planLabel = isActive ? 'Pro' : 'Free';
  const renewal = formatRenewalDate(entitlement?.access_end_at);
  const renewalLabel = status === 'trial_active'
    ? 'Trial ends'
    : (status === 'expired' ? 'Expired on' : 'Renews');

  const onManage = async () => {
    setPortalLoading(true);
    try {
      const res = await apiPost('/api/billing/create-portal-session', {
        origin_url: window.location.origin,
        return_url: `${window.location.origin}/app/settings`,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) throw new Error(data.detail || 'Could not open billing portal');
      await openExternal(data.url);
    } catch (e) {
      onFlashToast(typeof e?.message === 'string' ? e.message : 'Could not open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const onUpgrade = () => {
    onClose();
    navigate('/billing');
  };

  const onConfirmCancel = async () => {
    const res = await apiPost('/api/billing/cancel-subscription', { confirm_text: 'CANCEL' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail?.error || data.detail || 'Could not cancel subscription');
    }
    onFlashToast('Subscription cancelled.');
    setCancelOpen(false);
    setLoading(true);
    await fetchEntitlement();
  };

  return (
    <>
      <div className="hx-sheet-overlay" onClick={onClose}>
        <div className="hx-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <header className="hx-sheet-head">
            <h3 className="h-2">Subscription & Billing</h3>
            <button type="button" className="icon-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
          </header>
          <div className="hx-sheet-body">
            {loading ? (
              <p className="hx-info-body">Loading…</p>
            ) : (
              <>
                <div className="profile-billing-card">
                  <div className="billing-plan-row">
                    <span className="eyebrow">Current plan</span>
                    <span className={`billing-plan-label ${isActive ? 'is-pro' : ''}`}>{planLabel}</span>
                  </div>
                  {renewal && (
                    <div className="billing-plan-row">
                      <span className="eyebrow">{renewalLabel}</span>
                      <span className="billing-renewal mono">{renewal}</span>
                    </div>
                  )}
                </div>

                {hasSubscription ? (
                  <>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={onManage}
                      disabled={portalLoading}
                    >
                      {portalLoading ? (
                        <>
                          <span className="billing-spinner" aria-hidden /> Opening portal…
                        </>
                      ) : 'Manage subscription'}
                    </button>
                    <button
                      ref={cancelBtnRef}
                      type="button"
                      className="btn-ghost btn-danger-ghost"
                      onClick={() => setCancelOpen(true)}
                    >
                      Cancel subscription
                    </button>
                  </>
                ) : (
                  <button type="button" className="btn-primary" onClick={onUpgrade}>
                    Upgrade to Pro
                  </button>
                )}
              </>
            )}
          </div>
          <footer className="hx-sheet-foot single">
            <button type="button" className="btn-ghost" onClick={onClose}>Done</button>
          </footer>
        </div>
      </div>

      <ConfirmActionModal
        open={cancelOpen}
        title="Cancel subscription"
        message="Your subscription will be cancelled immediately and access will end now. This cannot be undone."
        confirmWord="CANCEL"
        confirmLabel="Cancel Subscription"
        destructive
        requirePassword={false}
        onClose={() => { setCancelOpen(false); setTimeout(() => cancelBtnRef.current?.focus(), 50); }}
        onConfirm={onConfirmCancel}
      />
    </>
  );
}

function NotifToggle({ id, label, desc, enabled, onChange }) {
  const labelId = `notif-label-${id}`;
  return (
    <button
      type="button"
      className={`notif-row ${enabled ? 'is-on' : 'is-off'}`}
      onClick={onChange}
      aria-pressed={enabled}
      aria-labelledby={labelId}
    >
      <span className="row-icon"><Bell size={18} color="var(--gold-500)" /></span>
      <span className="row-content">
        <span className="row-label" id={labelId}>{label}</span>
        {desc && <span className="row-desc eyebrow">{desc}</span>}
      </span>
      <span className={`notif-switch ${enabled ? 'is-on' : ''}`} aria-hidden>
        <span className="notif-knob" />
      </span>
    </button>
  );
}

export default function Profile({ userId, user, refreshMe }) {
  const navigate = useNavigate();
  const haptics = useHaptics();
  const [profile, setProfile] = useState({
    display_name: user?.display_name || '',
    role: user?.coach_name ? `Guided by ${user.coach_name}` : '',
    bio: user?.bio || '',
    avatar_url: user?.avatar_url || null,
  });
  const [signOutOpen, setSignOutOpen] = useState(false);
  const signOutBtnRef = useRef(null);
  const [streak, setStreak] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [notifPrefs, setNotifPrefs] = useState({
    morning_enabled: Boolean(user?.notification_prefs?.morning_enabled ?? true),
    evening_enabled: Boolean(user?.notification_prefs?.evening_enabled ?? true),
    weekly_review_enabled: Boolean(user?.notification_prefs?.weekly_review_enabled ?? true),
  });
  const [editOpen, setEditOpen] = useState(false);
  const [sheet, setSheet] = useState(null); // 'security' | 'data' | 'billing'
  const [toast, setToast] = useState(null);
  const persistInFlight = useRef(null);

  // Fix iOS-style sheet appearing scrolled — always start at top when opening.
  useEffect(() => {
    if (sheet) {
      try { window.scrollTo({ top: 0, behavior: 'instant' }); }
      catch { window.scrollTo(0, 0); }
    }
  }, [sheet]);

  // Also reset scroll on initial Profile mount in case the user lands here
  // with prior scroll state (e.g. returning from Stripe portal).
  useEffect(() => {
    try { window.scrollTo({ top: 0, behavior: 'instant' }); }
    catch { window.scrollTo(0, 0); }
  }, []);

  const flashToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  };

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const [meRes, streakRes, insightsRes] = await Promise.allSettled([
          apiGet(`/api/users/${userId}`),
          apiGet(`/api/streaks/${userId}`),
          apiGet(`/api/insights/${userId}`),
        ]);
        if (cancelled) return;
        if (meRes.status === 'fulfilled' && meRes.value?.ok) {
          const d = await meRes.value.json().catch(() => null);
          if (d) {
            setProfile((p) => ({
              ...p,
              display_name: d.display_name || p.display_name,
              role: d.coach_name ? `Guided by ${d.coach_name}` : p.role,
              bio: d.bio || p.bio,
              avatar_url: d.avatar_url || p.avatar_url,
            }));
            if (d.notification_prefs) {
              setNotifPrefs({
                morning_enabled: Boolean(d.notification_prefs.morning_enabled ?? true),
                evening_enabled: Boolean(d.notification_prefs.evening_enabled ?? true),
                weekly_review_enabled: Boolean(d.notification_prefs.weekly_review_enabled ?? true),
              });
            }
          }
        }
        if (streakRes.status === 'fulfilled' && streakRes.value?.ok) {
          const d = await streakRes.value.json().catch(() => null);
          if (d?.day_streak != null) setStreak(d.day_streak);
        }
        if (insightsRes.status === 'fulfilled' && insightsRes.value?.ok) {
          const d = await insightsRes.value.json().catch(() => null);
          if (d != null) {
            const pct = d.weekly_pct ?? d.completion_rate ?? d.completion_pct ?? d.completion ?? null;
            if (pct != null) setCompletion(Math.round(pct));
          }
        }
      } catch { /* keep null — show dashes */ }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const todayIdx = useMemo(() => todayWeekdayIdx(), []);

  const persistUser = async (patch) => {
    if (!userId) return;
    try { await apiPut(`/api/users/${userId}`, patch); } catch { /* swallow */ }
  };

  const onEditSave = async (data) => {
    setProfile((p) => ({ ...p, ...data }));
    setEditOpen(false);
    haptics.trigger('success');
    await persistUser(data);
    if (typeof refreshMe === 'function') refreshMe();
  };

  const toggleNotif = async (key) => {
    const next = { ...notifPrefs, [key]: !notifPrefs[key] };
    setNotifPrefs(next);
    haptics.trigger('light');
    if (persistInFlight.current) await persistInFlight.current.catch(() => {});
    persistInFlight.current = persistUser({ notification_prefs: next });
    await persistInFlight.current;
    persistInFlight.current = null;
  };

  const onSignOut = () => {
    haptics.trigger('warning');
    setSignOutOpen(true);
  };

  const onConfirmSignOut = async () => {
    try { await apiPost('/api/auth/logout', {}); } catch {}
    try {
      const native = await import('../lib/native');
      if (native.clearStoredToken) await native.clearStoredToken();
    } catch {}
    try { localStorage.clear(); } catch {}
    window.location.href = '/';
  };

  const streakDisplay = streak != null ? streak : '—';
  const completionDisplay = completion != null ? `${completion}%` : '—';

  return (
    <div className="page profile-page">
      <PageHeader back title="Settings" />

      <div className="stagger">
        <section className="profile-card">
          <div className="hx-avatar md">
            {profile.avatar_url
              ? <img src={profile.avatar_url} alt={`${profile.display_name} avatar`} />
              : <span aria-hidden>{getInitials(profile.display_name)}</span>}
          </div>
          <div className="profile-meta">
            <h1 className="h-1 profile-name">{profile.display_name || '—'}</h1>
            {profile.role && <div className="eyebrow profile-role">{profile.role}</div>}
            {profile.bio && <p className="profile-bio">{profile.bio}</p>}
          </div>
          <button
            type="button"
            className="profile-edit-btn"
            onClick={() => { haptics.trigger('light'); setEditOpen(true); }}
            aria-label="Edit profile"
          >
            <Edit size={14} />
            <span>Edit</span>
          </button>
        </section>

        <section className="profile-stats">
          <div className="streak-card black-card">
            <div className="streak-top">
              <div className="streak-num">{streakDisplay}</div>
              <div className="streak-label eyebrow-gold">DAY STREAK</div>
            </div>
            <div className="streak-week" aria-hidden>
              <div className="week-row">
                {WEEK_LETTERS.map((L, i) => (
                  <span key={`l-${i}`} className="week-letter">{L}</span>
                ))}
              </div>
              <div className="week-row">
                {WEEK_LETTERS.map((_, i) => (
                  <span key={`d-${i}`}
                    className={`week-dot${i === todayIdx ? ' is-today' : ''}${streak?.week_days?.[i] ? ' is-past' : ''}`} />
                ))}
              </div>
            </div>
          </div>

          <div className="completion-card">
            <div className="completion-ring">
              <Donut pct={completion ?? 0} />
              <div className="completion-pct mono">{completionDisplay}</div>
            </div>
            <div className="completion-meta">
              <div className="eyebrow completion-label">Weekly</div>
              <div className="completion-sub">completion</div>
            </div>
          </div>
        </section>

        <section className="profile-section">
          <div className="section-head"><span className="eyebrow">Notifications</span></div>
          <NotifToggle
            id="morning"
            label="Morning reminder"
            enabled={notifPrefs.morning_enabled}
            onChange={() => toggleNotif('morning_enabled')}
          />
          <NotifToggle
            id="evening"
            label="Evening reflection nudge"
            enabled={notifPrefs.evening_enabled}
            onChange={() => toggleNotif('evening_enabled')}
          />
          <NotifToggle
            id="weekly"
            label="Weekly review"
            enabled={notifPrefs.weekly_review_enabled}
            onChange={() => toggleNotif('weekly_review_enabled')}
          />
        </section>

        <section className="profile-section">
          <ul className="account-list">
            <li>
              <button className="list-row tall" onClick={() => { haptics.trigger('light'); setSheet('billing'); }}>
                <span className="row-icon ink"><Sparkle size={18} color="var(--ink-soft)" /></span>
                <span className="row-label">Subscription & Billing</span>
                <ChevronRight size={16} color="var(--ink-mute)" />
              </button>
              <div className="rule" aria-hidden />
            </li>
            <li>
              <button className="list-row tall" onClick={() => { haptics.trigger('light'); setSheet('security'); }}>
                <span className="row-icon ink"><Shield size={18} color="var(--ink-soft)" /></span>
                <span className="row-label">Account & Security</span>
                <ChevronRight size={16} color="var(--ink-mute)" />
              </button>
              <div className="rule" aria-hidden />
            </li>
            <li>
              <button className="list-row tall" onClick={() => { haptics.trigger('light'); setSheet('data'); }}>
                <span className="row-icon ink"><Database size={18} color="var(--ink-soft)" /></span>
                <span className="row-label">Data & Privacy</span>
                <ChevronRight size={16} color="var(--ink-mute)" />
              </button>
              <div className="rule" aria-hidden />
            </li>
            <li>
              <a
                href={`mailto:${SUPPORT_EMAIL}?subject=Hourtrix support`}
                className="list-row tall"
              >
                <span className="row-icon ink"><HelpCircle size={18} color="var(--ink-soft)" /></span>
                <span className="row-label">Help & Support</span>
                <ChevronRight size={16} color="var(--ink-mute)" />
              </a>
              <div className="rule" aria-hidden />
            </li>
            <li>
              <button ref={signOutBtnRef} className="list-row tall sign-out" onClick={onSignOut}>
                <span className="row-icon ink"><Lock size={18} color="var(--ink-mute)" /></span>
                <span className="row-label">Sign out</span>
              </button>
            </li>
          </ul>
        </section>

        <section className="profile-section">
          <div className="section-head"><span className="eyebrow">Legal</span></div>
          <ul className="account-list">
            <li>
              <a
                href={TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="list-row tall"
              >
                <span className="row-icon ink"><LinkIcon size={18} color="var(--ink-soft)" /></span>
                <span className="row-label">Terms of Service</span>
                <ChevronRight size={16} color="var(--ink-mute)" />
              </a>
              <div className="rule" aria-hidden />
            </li>
            <li>
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="list-row tall"
              >
                <span className="row-icon ink"><LinkIcon size={18} color="var(--ink-soft)" /></span>
                <span className="row-label">Privacy Policy</span>
                <ChevronRight size={16} color="var(--ink-mute)" />
              </a>
            </li>
          </ul>
          <div className="profile-version">Hourtrix v{pkg.version}</div>
        </section>
      </div>

      <EditModal
        open={editOpen}
        initial={profile}
        onClose={() => setEditOpen(false)}
        onSave={onEditSave}
      />

      {sheet === 'security' && (
        <SecuritySheet user={user} onClose={() => setSheet(null)} />
      )}
      {sheet === 'data' && (
        <DataSheet userId={userId} onClose={() => setSheet(null)} onFlashToast={flashToast} />
      )}
      {sheet === 'billing' && (
        <BillingSheet onClose={() => setSheet(null)} onFlashToast={flashToast} />
      )}

      <ConfirmActionModal
        open={signOutOpen}
        title="Sign out of Hourtrix?"
        message="You'll need to sign in again to access your data."
        confirmLabel="Sign out"
        destructive={false}
        requirePassword={false}
        requireTypedText={false}
        onClose={() => { setSignOutOpen(false); setTimeout(() => signOutBtnRef.current?.focus(), 50); }}
        onConfirm={onConfirmSignOut}
      />

      {toast && (
        <div className="profile-toast" role="status" aria-live="polite">{toast}</div>
      )}
    </div>
  );
}
